import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  EditNote as EditNoteIcon,
} from "@mui/icons-material";
import apiClient from "../../services/api";

const OperatorCallLeads = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({
    total_calls: 0,
    total_whatsapp: 0,
    interested_count: 0,
    not_interested_count: 0,
    answered_count: 0,
    not_answered_count: 0,
    not_reachable_count: 0,
    converted_sales: 0,
    daily: [],
  });
  const [statsRange, setStatsRange] = useState({
    period: "today",
    selectedDate: new Date().toISOString().split("T")[0],
  });
  const [filters, setFilters] = useState({
    searchQuery: "",
    callsFor: "",
    interestedOnly: false,
    leadFilter: "all",
  });
  const [selectedLead, setSelectedLead] = useState(null);
  const [focusLeadId, setFocusLeadId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogFeedback, setDialogFeedback] = useState({
    type: "",
    message: "",
  });
  const [form, setForm] = useState({
    callOutcome: "answered",
    interestedStatus: "",
    callRemark: "",
    convertToSales: false,
  });

  /** View Bills–style: API search string updates after a short pause while typing */
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchQuery(String(filters.searchQuery ?? "").trim());
    }, 320);
    return () => clearTimeout(t);
  }, [filters.searchQuery]);

  const fetchLeads = useCallback(
    async (immediateSearch, options = {}) => {
      const silent = Boolean(options.silent);
      if (!silent) setLoading(true);
      setError("");
      try {
        const f = filtersRef.current;
        const params = {};
        if (f.interestedOnly) params.interested_only = true;
        if (f.leadFilter && f.leadFilter !== "all") {
          params.lead_filter = f.leadFilter;
        }
        const q =
          typeof immediateSearch === "string"
            ? immediateSearch.trim()
            : String(debouncedSearchQuery || "").trim();
        if (q) {
          params.q = q;
          params.search = q;
          params.limit = 2000;
        }
        const callsFor = String(f.callsFor || "").trim();
        if (callsFor) {
          params.calls_for = callsFor;
          params.service = callsFor;
          params.limit = 2000;
        }

        // Important: avoid leading slash so axios correctly joins baseURL (`/api`)
        const response = await apiClient.get("bills/operator/leads", { params });
        const data = Array.isArray(response?.data?.data) ? response.data.data : [];
        setLeads(data);
      } catch (err) {
        const message = err?.response?.data?.message || "Failed to load operator leads";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearchQuery, filters.interestedOnly, filters.leadFilter, filters.callsFor],
  );

  const flushSearchAndFetchLeads = useCallback(() => {
    const s = String(filtersRef.current.searchQuery ?? "").trim();
    setDebouncedSearchQuery(s);
    void fetchLeads(s);
  }, [fetchLeads]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = {
        period: statsRange.period,
      };
      if (statsRange.period === "selected_date" && statsRange.selectedDate) {
        params.date = statsRange.selectedDate;
      }
      const response = await apiClient.get("bills/operator/stats", { params });
      const payload = response?.data?.data || {};
      setStats({
        total_calls: Number(payload.total_calls || 0),
        total_whatsapp: Number(payload.total_whatsapp || 0),
        interested_count: Number(payload.interested_count || 0),
        not_interested_count: Number(payload.not_interested_count || 0),
        answered_count: Number(payload.answered_count || 0),
        not_answered_count: Number(payload.not_answered_count || 0),
        not_reachable_count: Number(payload.not_reachable_count || 0),
        converted_sales: Number(payload.converted_sales || 0),
        daily: Array.isArray(payload.daily) ? payload.daily : [],
      });
    } catch (err) {
      // Keep page usable even if stats endpoint fails
      setStats((prev) => ({ ...prev, daily: [] }));
    } finally {
      setStatsLoading(false);
    }
  }, [statsRange.period, statsRange.selectedDate]);

  useEffect(() => {
    void fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const openUpdateDialog = (lead) => {
    setSelectedLead(lead);
    setDialogFeedback({ type: "", message: "" });
    setForm({
      callOutcome: lead?.call_outcome || "answered",
      interestedStatus: lead?.interested_status || "",
      callRemark: lead?.call_remark || "",
      convertToSales: Boolean(lead?.converted_to_sales),
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedLead(null);
    setDialogFeedback({ type: "", message: "" });
  };

  const updateLead = async (leadId, payload) => {
    setUpdating(true);
    setError("");
    setSuccess("");
    try {
      // Important: avoid leading slash so axios correctly joins baseURL (`/api`)
      await apiClient.patch(`bills/${leadId}/operator-call`, payload);
      setSuccess("Call details updated");
      await Promise.all([
        fetchLeads(filtersRef.current.searchQuery, { silent: true }),
        fetchStats(),
      ]);
      return { ok: true, message: "Saved successfully" };
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      const apiError = err?.response?.data?.error;
      const message = [apiMessage, apiError].filter(Boolean).join(" - ") || "Failed to update call details";
      setError(message);
      return { ok: false, message };
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveDialog = async () => {
    if (!selectedLead) return;
    if (
      form.convertToSales &&
      (form.callOutcome !== "answered" || form.interestedStatus !== "interested")
    ) {
      setDialogFeedback({
        type: "error",
        message: "Convert to Sales can be enabled only when call result is Answered and status is Interested.",
      });
      return;
    }
    const currentIndex = leads.findIndex(
      (item) => String(item.bill_id) === String(selectedLead.bill_id),
    );
    const nextLeadId =
      currentIndex >= 0 && currentIndex + 1 < leads.length
        ? leads[currentIndex + 1].bill_id
        : null;
    const result = await updateLead(selectedLead.bill_id, {
      call_outcome: form.callOutcome,
      interested_status: form.callOutcome === "answered" ? (form.interestedStatus || null) : null,
      call_remark: form.callRemark || null,
      converted_to_sales: Boolean(form.convertToSales),
      phone: selectedLead.mobile,
      apply_to_phone: false,
      call_topic: String(filters.callsFor || selectedLead.calls_for || "").trim() || undefined,
      calls_for: String(filters.callsFor || selectedLead.calls_for || "").trim() || undefined,
    });
    if (result.ok) {
      setDialogFeedback({ type: "success", message: result.message });
      setTimeout(() => {
        closeDialog();
        if (nextLeadId) {
          setFocusLeadId(String(nextLeadId));
          window.setTimeout(() => {
            document
              .getElementById(`operator-lead-${nextLeadId}`)
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 80);
        }
      }, 400);
    } else {
      setDialogFeedback({ type: "error", message: result.message });
    }
  };

  const trackActivity = async (lead, options = { call: false, whatsapp: false }) => {
    if (!lead?.bill_id) return;
    const topic = String(filters.callsFor || "").trim();
    if (options.call) {
      setLeads((prev) =>
        prev.map((item) => {
          if (item.bill_id !== lead.bill_id) return item;
          return {
            ...item,
            call_attempts: Number(item.call_attempts || 0) + 1,
            called_for_current_topic: topic ? true : item.called_for_current_topic,
            current_topic_call: topic
              ? {
                  ...(item.current_topic_call || {}),
                  topic,
                  called: true,
                  called_at: new Date().toISOString(),
                  call_outcome: item.current_topic_call?.call_outcome || null,
                }
              : item.current_topic_call,
          };
        }),
      );
    }
    try {
      await apiClient.patch(`bills/${lead.bill_id}/operator-call`, {
        mark_call_attempted: Boolean(options.call),
        mark_whatsapp_contacted: Boolean(options.whatsapp),
        phone: lead.mobile,
        apply_to_phone: false,
        ...(topic ? { call_topic: topic, calls_for: topic } : {}),
      });
      await fetchStats();
    } catch (err) {
      // Tracking failure should not block user action
    }
  };

  const openDialer = async (lead) => {
    const phone = lead?.mobile;
    if (!phone) return;
    trackActivity(lead, { call: true, whatsapp: false });
    window.open(`tel:${phone}`, "_self");
  };

  const openWhatsApp = async (lead) => {
    const phone = lead?.whatsapp_number || lead?.mobile;
    if (!phone) return;
    await trackActivity(lead, { call: false, whatsapp: true });
    const clean = String(phone).replace(/\D/g, "");
    window.open(`https://wa.me/91${clean}`, "_blank", "noopener,noreferrer");
  };

  const getLeadStatusMeta = useMemo(
    () => (lead) => {
      const topic = String(filters.callsFor || "").trim();
      const outcomeRaw = topic
        ? lead?.current_topic_call?.call_outcome || ""
        : lead?.call_outcome || "";
      const normalizedOutcome = String(outcomeRaw)
        .trim()
        .toLowerCase()
        .replace(/[-\s]+/g, "_");
      const normalizedInterest = String(
        topic
          ? lead?.current_topic_call?.interested_status || lead?.interested_status || ""
          : lead?.interested_status || "",
      )
        .trim()
        .toLowerCase()
        .replace(/[-\s]+/g, "_");
      const called = topic
        ? Boolean(lead?.called_for_current_topic)
        : Number(lead?.call_attempts || 0) > 0 ||
          Boolean(lead?.call_completed) ||
          Boolean(lead?.call_attempted_at);
      const converted = Boolean(
        topic
          ? lead?.current_topic_call?.converted_to_sales || lead?.converted_to_sales
          : lead?.converted_to_sales,
      );

      if (normalizedOutcome === "answered") {
        return {
          kind: "answered",
          label: converted
            ? "Answered · Converted"
            : normalizedInterest === "interested"
              ? "Answered + Interested"
              : normalizedInterest === "not_interested"
                ? "Answered + Not Interested"
                : "Answered",
          fg: "#166534",
          bg: "#DCFCE7",
          border: "#86EFAC",
          rowBg: "#F0FDF4",
          text: "#166534",
          subText: "#15803D",
          buttonBg: "#16A34A",
          buttonFg: "#FFFFFF",
        };
      }
      if (normalizedOutcome === "not_answered") {
        return {
          kind: "not_answered",
          label: "Not Answered",
          fg: "#991B1B",
          bg: "#FEE2E2",
          border: "#FCA5A5",
          rowBg: "#FEF2F2",
          text: "#991B1B",
          subText: "#B91C1C",
          buttonBg: "#DC2626",
          buttonFg: "#FFFFFF",
        };
      }
      if (normalizedOutcome === "not_reachable") {
        return {
          kind: "not_reachable",
          label: "Not Reachable",
          fg: "#000000",
          bg: "#FEF08A",
          border: "#EAB308",
          rowBg: "#FEF08A",
          text: "#000000",
          subText: "#000000",
          buttonBg: "#FACC15",
          buttonFg: "#000000",
        };
      }
      if (called) {
        return {
          kind: "tried",
          label: "Tried Call",
          fg: "#C2410C",
          bg: "#FFEDD5",
          border: "#FB923C",
          rowBg: "#FFF7ED",
          text: "#9A3412",
          subText: "#C2410C",
          buttonBg: "#EA580C",
          buttonFg: "#FFFFFF",
        };
      }
      return {
        kind: "fresh",
        label: "Not Updated",
        fg: "#475569",
        bg: "#F1F5F9",
        border: "#CBD5E1",
        rowBg: "inherit",
        text: "#0F172A",
        subText: "#334155",
        buttonBg: "",
        buttonFg: "",
      };
    },
    [filters.callsFor],
  );

  const statusChip = useMemo(
    () => (lead) => {
      const meta = getLeadStatusMeta(lead);
      if (meta.kind === "fresh") return null;
      return (
        <Chip
          label={meta.label}
          size="small"
          sx={{
            color: meta.fg,
            backgroundColor: meta.bg,
            border: `1px solid ${meta.border}`,
            fontWeight: 700,
          }}
        />
      );
    },
    [getLeadStatusMeta],
  );

  const renderCallButton = useMemo(
    () => (lead, { compact = false } = {}) => {
      const meta = getLeadStatusMeta(lead);
      const isFresh = meta.kind === "fresh";
      return (
        <Button
          size="small"
          variant={isFresh ? "outlined" : "contained"}
          startIcon={compact ? null : <PhoneIcon />}
          onClick={() => openDialer(lead)}
          sx={
            isFresh
              ? undefined
              : {
                  backgroundColor: meta.buttonBg,
                  color: meta.buttonFg,
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: meta.buttonBg,
                    filter: "brightness(0.95)",
                    boxShadow: "none",
                  },
                }
          }
        >
          {compact ? "Call" : "Call"}
        </Button>
      );
    },
    [getLeadStatusMeta],
  );

  const filteredLeads = leads;

  const filteredStats = useMemo(() => {
    const stats = {
      records: filteredLeads.length,
      interested: 0,
      notInterested: 0,
      answered: 0,
      notAnswered: 0,
      notReachable: 0,
      converted: 0,
    };

    filteredLeads.forEach((lead) => {
      const outcome = String(lead?.call_outcome || "")
        .trim()
        .toLowerCase()
        .replace(/[-\s]+/g, "_");
      const interest = String(lead?.interested_status || "")
        .trim()
        .toLowerCase()
        .replace(/[-\s]+/g, "_");

      if (lead?.converted_to_sales) stats.converted += 1;
      if (outcome === "answered") stats.answered += 1;
      if (outcome === "not_answered") stats.notAnswered += 1;
      if (outcome === "not_reachable") stats.notReachable += 1;
      if (interest === "interested") stats.interested += 1;
      if (interest === "not_interested") stats.notInterested += 1;
    });

    return stats;
  }, [filteredLeads]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Operator Call Leads
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Filter by service/profession and update call completion, interest, and remarks.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={flushSearchAndFetchLeads}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Calls for (topic)"
                placeholder="e.g. RRB Jobs, Admission"
                helperText="Filters bills like View Bills and saves call status for this topic only"
                value={filters.callsFor}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, callsFor: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                label="Search name / phone / any info"
                helperText="Optional extra filter within the Calls-for list"
                value={filters.searchQuery}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, searchQuery: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent={{ xs: "flex-start", md: "space-between" }}
                sx={{ height: "100%" }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.interestedOnly}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          interestedOnly: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Interested only"
                />
                <Button variant="contained" onClick={flushSearchAndFetchLeads} disabled={loading}>
                  Search now
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Lead Filter"
                value={filters.leadFilter}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, leadFilter: e.target.value }))
                }
                SelectProps={{ native: true }}
              >
                <option value="all">All Leads</option>
                <option value="uncalled">Uncalled Numbers</option>
                <option value="interested">Interested</option>
                <option value="not_interested">Not Interested</option>
                <option value="converted">Converted to Sales</option>
              </TextField>
            </Grid>
          </Grid>
          <Box
            sx={{
              mt: 2,
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              border: "1px solid #CBD5E1",
              background:
                "linear-gradient(90deg, rgba(241,245,249,0.95), rgba(248,250,252,0.95))",
            }}
          >
            <Typography
              variant="caption"
              sx={{ display: "block", mb: 1, color: "#334155", fontWeight: 700 }}
            >
              Filtered Results (Live)
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={`Records: ${filteredStats.records}`}
                sx={{ bgcolor: "#DBEAFE", color: "#1E3A8A", fontWeight: 700 }}
              />
              <Chip
                label={`Interested: ${filteredStats.interested}`}
                sx={{ bgcolor: "#DCFCE7", color: "#166534", fontWeight: 700 }}
              />
              <Chip
                label={`Not Interested: ${filteredStats.notInterested}`}
                sx={{ bgcolor: "#FEE2E2", color: "#991B1B", fontWeight: 700 }}
              />
              <Chip
                label={`Answered: ${filteredStats.answered}`}
                sx={{ bgcolor: "#DCFCE7", color: "#166534", fontWeight: 700 }}
              />
              <Chip
                label={`Not Answered: ${filteredStats.notAnswered}`}
                sx={{ bgcolor: "#FEE2E2", color: "#991B1B", fontWeight: 700 }}
              />
              <Chip
                label={`Not Reachable: ${filteredStats.notReachable}`}
                sx={{ bgcolor: "#FEF08A", color: "#000000", fontWeight: 700 }}
              />
              <Chip
                label={`Converted: ${filteredStats.converted}`}
                sx={{ bgcolor: "#F3E8FF", color: "#6D28D9", fontWeight: 700 }}
              />
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Call Performance Statistics
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
            <TextField
              select
              size="small"
              label="Stats Range"
              value={statsRange.period}
              onChange={(e) =>
                setStatsRange((prev) => ({ ...prev, period: e.target.value }))
              }
              SelectProps={{ native: true }}
              sx={{ minWidth: 220 }}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="selected_date">Selected Date</option>
            </TextField>
            {statsRange.period === "selected_date" ? (
              <TextField
                size="small"
                type="date"
                label="Date"
                value={statsRange.selectedDate}
                onChange={(e) =>
                  setStatsRange((prev) => ({ ...prev, selectedDate: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
            ) : null}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Chip color="primary" label={`Total Calls: ${stats.total_calls}`} />
            <Chip color="success" label={`Total WhatsApp: ${stats.total_whatsapp}`} />
            <Chip color="info" label={`Interested: ${stats.interested_count}`} />
            <Chip color="default" label={`Not Interested: ${stats.not_interested_count}`} />
            <Chip color="primary" variant="outlined" label={`Answered: ${stats.answered_count}`} />
            <Chip color="default" variant="outlined" label={`Not Answered: ${stats.not_answered_count}`} />
            <Chip color="warning" variant="outlined" label={`Not Reachable: ${stats.not_reachable_count}`} />
            <Chip color="secondary" label={`Converted Sales: ${stats.converted_sales}`} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Day-wise Calls / WhatsApp
          </Typography>
          {statsLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading statistics...
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {stats.daily.map((item) => (
                <Chip
                  key={item.day}
                  variant="outlined"
                  label={`${new Date(item.day).toLocaleDateString()} - C:${item.calls} / WA:${item.whatsapp}`}
                />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Grid container spacing={2}>
          {filteredLeads.map((lead) => (
            <Grid item xs={12} key={lead.bill_id}>
              {(() => {
                const meta = getLeadStatusMeta(lead);
                return (
              <Card
                id={`operator-lead-${lead.bill_id}`}
                sx={{
                  bgcolor: meta.rowBg,
                  border: String(focusLeadId) === String(lead.bill_id)
                    ? "2px solid #4F46E5"
                    : `1px solid ${meta.border}`,
                  boxShadow: String(focusLeadId) === String(lead.bill_id)
                    ? "0 0 0 3px rgba(79,70,229,0.18)"
                    : undefined,
                }}
              >
                <CardContent>
                  <Typography variant="h6" fontWeight={700} sx={{ color: meta.text }}>
                    {lead.customer_name || "Unknown"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: meta.subText }}>
                    Service: {lead.services || "-"}
                  </Typography>
                  {String(filters.callsFor || "").trim() ? (
                    <Alert
                      severity={
                        meta.kind === "answered"
                          ? "success"
                          : meta.kind === "not_answered"
                            ? "error"
                            : meta.kind === "fresh"
                              ? "warning"
                              : "info"
                      }
                      sx={{
                        mt: 1,
                        py: 0,
                        backgroundColor: meta.kind === "fresh" ? undefined : meta.bg,
                        color: meta.text,
                        border: `1px solid ${meta.border}`,
                        "& .MuiAlert-icon": { color: meta.fg },
                      }}
                    >
                      {lead.called_for_current_topic
                        ? `${meta.label} for “${filters.callsFor}”`
                        : `Not called for “${filters.callsFor}” yet`}
                      {Array.isArray(lead.topic_history) && lead.topic_history.length > 0
                        ? ` · Earlier: ${lead.topic_history
                            .map((t) => t.topic)
                            .filter(Boolean)
                            .join(", ")}`
                        : ""}
                    </Alert>
                  ) : null}
                  <Typography variant="body2" sx={{ color: meta.subText }}>
                    Bill Date:{" "}
                    {lead.bill_date || lead.order_date
                      ? new Date(lead.bill_date || lead.order_date).toLocaleString()
                      : "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: meta.subText }}>
                    Created Date:{" "}
                    {lead.created_at ? new Date(lead.created_at).toLocaleString() : "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: meta.subText }}>Profession: {lead.profession || "-"}</Typography>
                  <Typography variant="body2" sx={{ color: meta.subText }}>City: {lead.city || "-"}</Typography>
                  <Typography variant="body2" sx={{ color: meta.subText }}>Calls Done: {lead.call_attempts || 0}</Typography>
                  <Typography variant="body2" sx={{ color: meta.subText }}>WhatsApp Follow-ups: {lead.whatsapp_attempts || 0}</Typography>
                  <Typography variant="body2" sx={{ color: meta.subText }}>
                    Called At:{" "}
                    {lead.call_completed_at
                      ? new Date(lead.call_completed_at).toLocaleString()
                      : "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: meta.subText }}>
                    Called By: {lead.called_by_operator_id || "-"}
                  </Typography>
                  <Box sx={{ mt: 1 }}>{statusChip(lead)}</Box>
                  {lead.call_remark ? (
                    <Typography variant="body2" sx={{ mt: 1, color: meta.subText }}>
                      Remark: {lead.call_remark}
                    </Typography>
                  ) : null}
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                    {renderCallButton(lead)}
                    <Button
                      size="small"
                      color="success"
                      startIcon={<WhatsAppIcon />}
                      onClick={() => openWhatsApp(lead)}
                    >
                      WhatsApp
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditNoteIcon />}
                      onClick={() => openUpdateDialog(lead)}
                    >
                      Update
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
                );
              })()}
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Card}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Profession</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Call</TableCell>
                <TableCell>WhatsApp</TableCell>
                <TableCell>Call Attempts</TableCell>
                <TableCell>WA Attempts</TableCell>
                <TableCell>Called By</TableCell>
                <TableCell>Called At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Remark</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLeads.map((lead) => (
                (() => {
                  const meta = getLeadStatusMeta(lead);
                  return (
                <TableRow
                  key={lead.bill_id}
                  id={`operator-lead-${lead.bill_id}`}
                  sx={{
                    bgcolor: meta.rowBg,
                    outline: String(focusLeadId) === String(lead.bill_id)
                      ? "2px solid #4F46E5"
                      : "none",
                    "& .MuiTableCell-root": {
                      color: meta.text,
                      borderColor: meta.border,
                    },
                  }}
                >
                  <TableCell>{lead.customer_name || "-"}</TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2">{lead.services || "-"}</Typography>
                    <Typography variant="caption" sx={{ display: "block", color: meta.subText, mt: 0.5 }}>
                      Date:{" "}
                      {(lead.bill_date || lead.order_date)
                        ? new Date(lead.bill_date || lead.order_date).toLocaleDateString("en-IN")
                        : "-"}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", color: meta.subText }}>
                      Created:{" "}
                      {lead.created_at ? new Date(lead.created_at).toLocaleDateString("en-IN") : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>{lead.profession || "-"}</TableCell>
                  <TableCell>{lead.city || "-"}</TableCell>
                  <TableCell>{lead.mobile || "-"}</TableCell>
                  <TableCell>{lead.whatsapp_number || lead.mobile || "-"}</TableCell>
                  <TableCell>{lead.call_attempts || 0}</TableCell>
                  <TableCell>{lead.whatsapp_attempts || 0}</TableCell>
                  <TableCell>{lead.called_by_operator_id || "-"}</TableCell>
                  <TableCell>
                    {lead.call_completed_at
                      ? new Date(lead.call_completed_at).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell>{statusChip(lead)}</TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>{lead.call_remark || "-"}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {renderCallButton(lead, { compact: true })}
                      <Button
                        size="small"
                        color="success"
                        onClick={() => openWhatsApp(lead)}
                      >
                        WA
                      </Button>
                      <Button size="small" onClick={() => openUpdateDialog(lead)}>
                        Edit
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
                  );
                })()
              ))}
              {!filteredLeads.length && (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Update Call Outcome</DialogTitle>
        <DialogContent dividers>
          {dialogFeedback.message ? (
            <Alert severity={dialogFeedback.type || "info"} sx={{ mb: 1.5 }}>
              {dialogFeedback.message}
            </Alert>
          ) : null}
          <Typography variant="body2" sx={{ mb: 1 }}>
            Customer: {selectedLead?.customer_name || "-"}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Mobile: {selectedLead?.mobile || "-"}
          </Typography>
          {String(filters.callsFor || selectedLead?.calls_for || "").trim() ? (
            <Alert
              severity={selectedLead?.called_for_current_topic ? "success" : "warning"}
              sx={{ mb: 1.5 }}
            >
              {selectedLead?.called_for_current_topic
                ? `Already called for “${filters.callsFor || selectedLead?.calls_for}”. You can update the result.`
                : `Not called yet for “${filters.callsFor || selectedLead?.calls_for}”.`}
              {Array.isArray(selectedLead?.topic_history) && selectedLead.topic_history.length > 0
                ? ` Earlier topics: ${selectedLead.topic_history
                    .map((t) => t.topic)
                    .filter(Boolean)
                    .join(", ")}.`
                : ""}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Tip: set “Calls for” (e.g. RRB Jobs) so this update is saved topic-wise.
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Call Result
          </Typography>
          <RadioGroup
            value={form.callOutcome}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                callOutcome: e.target.value,
                interestedStatus: e.target.value === "answered" ? prev.interestedStatus : "",
              }))
            }
          >
            <FormControlLabel value="answered" control={<Radio />} label="Answered" />
            {form.callOutcome === "answered" ? (
              <Box sx={{ ml: 3, mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5, mt: -0.5, fontWeight: 600 }}>
                  Interest Status (only for answered calls)
                </Typography>
                <RadioGroup
                  value={form.interestedStatus}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      interestedStatus: e.target.value,
                    }))
                  }
                >
                  <FormControlLabel value="interested" control={<Radio />} label="Interested" />
                  <FormControlLabel
                    value="not_interested"
                    control={<Radio />}
                    label="Not Interested"
                  />
                </RadioGroup>
              </Box>
            ) : null}
            <FormControlLabel value="not_answered" control={<Radio />} label="Not Answered" />
            <FormControlLabel value="not_reachable" control={<Radio />} label="Not Reachable" />
          </RadioGroup>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Remarks"
            value={form.callRemark}
            onChange={(e) => setForm((prev) => ({ ...prev, callRemark: e.target.value }))}
          />
          {selectedLead?.services ? (
            <Typography variant="body2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600 }}>
              Services on bill: {selectedLead.services}
            </Typography>
          ) : null}
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                color="secondary"
                checked={Boolean(form.convertToSales)}
                disabled={
                  updating ||
                  form.callOutcome !== "answered" ||
                  form.interestedStatus !== "interested"
                }
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    convertToSales: e.target.checked,
                  }))
                }
              />
            }
            label="Convert to Sales"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: -0.5 }}>
            Enabled only for Answered + Interested leads to avoid accidental conversions.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveDialog}
            disabled={updating}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperatorCallLeads;
